import React from 'react';
import { ChatContextType } from '@components/Chat/ChatContextProvider/ChatContextType';
import { ChatUnreadPayload } from '@components/Chat/ChatChannel/ChatChannelType';
import { ChatChannel } from '@components/Chat/ChatChannel/ChatChannel';
import { useChatBuilderConfig } from '@components/Chat/hooks/useChatBuilderConfig/useChatBuilderConfig';
import { useGetChatAuthToken } from '@data/liveChat/hooks/useGetChatAuthToken/useGetChatAuthToken';
import { NOTIFICATION_RECEIVE_AUDIO_URL } from '@utilities/constants';
import { ChatMessageType, MessageEventData } from '@components/Chat/ChatChannel/ChatChannelType';
import { mergeUrl } from '@utilities/mergeUrl';
import { useGetNotificationToken } from '@utilities/hooks/useGetNotificationToken/useGetNotificationToken';
import { useNotificationPermission } from '@utilities/hooks/useNotificationPermission/useNotificationPermission';

const ChatContext = React.createContext<null | ChatContextType>(null);

export const useChatContext = () => {
	const ctx = React.useContext(ChatContext);
	if (!ctx) {
		throw new Error(
			'useChatContext must be used within a child component of ChatContextProvider',
		);
	}
	return ctx;
};

const isValidOrigin = (event: MessageEvent) => {
	return event.origin === new URL(window.config.CHAT_UI_URL).origin;
};

const getEventDataMessage = (event: MessageEvent<MessageEventData>) => {
	if (!event.data || !event.data.message) return null;
	return event.data.message;
};

export const ChatContextProvider: React.FC = ({ children }) => {
	const [showChat, setShowChat] = React.useState(false);
	const [chatUnreadCount, setChatUnreadCount] = React.useState<number | null>(null);
	const [chatChannel, setChatChannel] = React.useState<ChatChannel | null>(null);

	const { requestNotificationPermission } = useNotificationPermission();
	const chatBuilderConfig = useChatBuilderConfig();
	const { authToken } = useGetChatAuthToken();
	const { notificationToken } = useGetNotificationToken();
	const { chatFeatures } = chatBuilderConfig || {};
	const notificationReceiveAudioRef = React.useRef<HTMLAudioElement | null>(null);
	const unreadRef = React.useRef<number | null>(null);
	const hasChatAutoOpenedRef = React.useRef(false);

	const handleMessage = React.useCallback((event: MessageEvent<MessageEventData>) => {
		if (!isValidOrigin(event)) return;

		if (getEventDataMessage(event) === ChatMessageType.CHAT_HANDSHAKE_INIT_MESSAGE) {
			event.source?.postMessage(
				{ message: ChatMessageType.CHAT_HANDSHAKE_RESPONSE_MESSAGE },
				{ targetOrigin: event.origin },
			);
			return;
		}

		if (getEventDataMessage(event) === ChatMessageType.CHAT_PORT_MESSAGE) {
			const [port] = event.ports;

			if (!port) return;
			port.start();

			const channel = new ChatChannel(port);
			setChatChannel(channel);
		}
	}, []);

	const handleServiceWorkerMessage = React.useCallback((event: MessageEvent<{ message?: unknown }>) => {
		if (event.data && event.data.message) {
			setShowChat(true);
		}
	}, []);

	React.useEffect(() => {
		if (chatBuilderConfig) {
			void requestNotificationPermission();
		}
	}, [chatBuilderConfig, requestNotificationPermission]);

	React.useEffect(() => {
		const audio = new Audio(`${mergeUrl(NOTIFICATION_RECEIVE_AUDIO_URL)}`);
		audio.preload = 'auto';
		notificationReceiveAudioRef.current = audio;
	}, []);

	React.useEffect(() => {
		window.addEventListener('message', handleMessage);
		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, [handleMessage]);

	React.useEffect(() => {
		if ('serviceWorker' in navigator && navigator.serviceWorker) {
			navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
			return () => {
				navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
			};
		}
	}, [handleServiceWorkerMessage]);

	const playReceiveNotificationSound = React.useCallback(async () => {
		await notificationReceiveAudioRef.current?.play();
	}, []);

	React.useEffect(() => {
		if (!chatChannel) return;

		const unsubscribe = chatChannel.onChatCount((payload: ChatUnreadPayload) => {
			const { count = 0, playSound = false } = payload;
			const shouldNotifyReceive =
				!chatFeatures?.coreMessagingExperience?.disableReceiveMessageSoundNotification;
			const countIncreased =
				(!unreadRef.current && count > 0) ||
				(unreadRef.current && unreadRef.current < count);
			if (shouldNotifyReceive && (playSound || countIncreased)) {
				void playReceiveNotificationSound();
			}

			// Only auto-open on the first count after channel init, if count > 0
			if (!hasChatAutoOpenedRef.current) {
				if (count > 0) {
					setShowChat(true);
				}
				hasChatAutoOpenedRef.current = true;
			}

			setChatUnreadCount(count);
			unreadRef.current = count;
		});
		return () => unsubscribe();
	}, [chatChannel, chatFeatures, playReceiveNotificationSound]);

	React.useEffect(() => {
		if (chatChannel && chatBuilderConfig && authToken) {
			chatChannel.sendBuilderConfig({
				version: 1,
				payload: {
					...chatBuilderConfig,
					chatFeatures: {
						...chatBuilderConfig.chatFeatures,
						coreMessagingExperience: {
							...chatBuilderConfig.chatFeatures.coreMessagingExperience,
							disableReceiveMessageSoundNotification: true,
						},
					},
				},
			});
			chatChannel.sendAuthToken({ version: 1, payload: { token: authToken } });
		}
	}, [chatChannel, chatBuilderConfig, authToken]);

	React.useEffect(() => {
		if (chatChannel && notificationToken) {
			chatChannel.sendNotificationToken({
				version: 1,
				payload: { token: notificationToken },
			});
		}
	}, [chatChannel, notificationToken]);

	const toggleShowChat = () => {
		setShowChat((prev) => !prev);
	};

	return (
		<ChatContext.Provider
			value={{
				showChat,
				toggleShowChat,
				setChatUnreadCount,
				chatUnreadCount,
				chatAuthToken: authToken,
			}}
		>
			{children}
		</ChatContext.Provider>
	);
};
